/**
 * @fileoverview PDF form filling — the pure decisions behind `fillPdfAction`.
 *
 * Everything the fill actions do that is not "talk to Convex or pdf-lib":
 * which value a placement renders, whether a checkbox is ticked, how a date
 * or a boxed (per-character) value is formatted and sliced, the geometry of a
 * centred mark or scaled signature image, and the key bookkeeping the
 * AcroForm/XFA paths need. `fillPdfAction.ts` keeps the document loading,
 * drawing and storage and calls into this module for the decisions.
 *
 * Free of Convex `ctx` and of pdf-lib document objects, so it is unit
 * testable directly (`fillPdfCore.test.ts`) — `fillPdfAction.ts` is a
 * `"use node"` action file that convex-test cannot run.
 */

import {
  resolveDateCalc,
  type DateCalc,
  type DateCalcContext,
} from "@cvx/settings/precedents/dateCalc";
import {
  resolveSuppressedFieldKeys,
  type FieldConditionMap,
} from "@cvx/jobs/precedents/fieldConditions";
import { sydneyDateIso } from "@cvx/lib/sydneyDate";
import {
  calendarDateDigits,
  formatDayFirst,
  parseCalendarDate,
  parseIsoTimestampUtcDate,
} from "@cvx/lib/calendarDate";
import { CANONICAL_FIELDS_MAP } from "@cvx/settings/precedents/canonicalFields";
import { parseContactFieldKey } from "@cvx/jobs/precedents/contactFields";

/** A rectangle placed on the template by the visual PDF field editor. */
export interface PdfFieldPlacement {
  id?: string;
  canonicalKey: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize?: number;
  fieldType?: string;
  charBoxes?: number;
  charStart?: number;
  checkboxValue?: string;
  defaultChecked?: boolean;
  groupKey?: string;
  groupLabel?: string;
  optionValue?: string;
  dateCalc?: DateCalc;
}

/** One AcroForm field name mapped to a canonical key. */
export interface PdfFieldMapping {
  pdfFieldName: string;
  canonicalKey?: string;
  fieldType: string;
  checkboxValue?: string;
}

export const SIG_IMAGE_PREFIX = "__sig_image__:";

/**
 * Canonical keys that fall back to today's date when nothing is entered.
 * @internal — exported for unit tests
 */
export const AUTO_DATE_KEYS = new Set([
  "letter_date",
  "signature_date",
  "submission_date",
]);

/** True for the PNG magic number, so the caller embeds PNG rather than JPEG. */
export function isPng(bytes: ArrayBuffer): boolean {
  const arr = new Uint8Array(bytes);
  return (
    arr[0] === 0x89 && arr[1] === 0x50 && arr[2] === 0x4e && arr[3] === 0x47
  );
}

/** True for the "__sig_image__:<url>" token a signature field carries. */
export function isSignatureToken(value: string | undefined): boolean {
  return !!value && value.startsWith(SIG_IMAGE_PREFIX);
}

/**
 * Render a date value as DD/MM/YYYY, leaving everything else alone.
 *
 * Exactly two shapes are dates (MOR-2342): a complete day-first or ISO
 * calendar date, read arithmetically (never through `new Date(string)`, which
 * would take "05/09/2021" as 9 May and can shift a day across the runtime
 * timezone), and an explicit ISO 8601 timestamp, rendered as its UTC calendar
 * date. Anything else — an impossible "2024-02-31", ordinary text like
 * "A/B 12", a stray "1", a partial "1/2/34" — is returned unchanged, so no
 * runtime-dependent date is ever manufactured from a typo.
 */
export function formatDateValue(value: string): string {
  const calendar = parseCalendarDate(value) ?? parseIsoTimestampUtcDate(value);
  return calendar ? formatDayFirst(calendar) : value;
}

/**
 * The character stream a TYPED date value feeds into boxed cells — shared by
 * the fill and the editor preview so both address the same digits
 * (MOR-2342). A real calendar date becomes DDMMYYYY; a valid timestamp
 * becomes the digits of its UTC calendar date; anything else — including a
 * calendar-shaped but impossible "2024-02-31" — keeps its own digits in the
 * order written, so an invalid entry is visible rather than replaced by an
 * invented date or silently dropped.
 *
 * @example
 * dateBoxDigits("05/09/2021"); // "05092021"
 * dateBoxDigits("2024-02-31"); // "20240231" (invalid: raw digits, not 2 March)
 * dateBoxDigits("2026-08-26T23:30:00Z"); // "26082026"
 */
export function dateBoxDigits(value: string): string {
  return formatDateValue(value).replace(/\D/g, "");
}

/**
 * Sydney "today" (yyyy-MM-dd), not the UTC timestamp — the action runs in
 * UTC, which is a day behind Sydney every local morning (MOR-1031).
 */
export function todayIso(): string {
  return sydneyDateIso();
}

/**
 * Parse a signature token into its URL + the signatory's size multiplier
 * (MOR-845). Tokens without a scale segment parse with scale 1, so the
 * behaviour for existing documents is unchanged.
 */
export function parseSigToken(value: string): {
  url: string;
  userScale: number;
} {
  const raw = value.slice(SIG_IMAGE_PREFIX.length);
  const match = /^s=([0-9]+(?:\.[0-9]+)?);/.exec(raw);
  if (!match) return { url: raw, userScale: 1 };
  const parsed = Number(match[1]);
  const userScale =
    Number.isFinite(parsed) && parsed > 0
      ? Math.min(Math.max(parsed, 0.5), 1.5)
      : 1;
  return { url: raw.slice(match[0].length), userScale };
}

/**
 * Decide whether a checkbox placement should be ticked.
 *
 * Three cases, resolved in order:
 *
 * 1. **Group member** (`groupKey` + `optionValue`): tick when the group
 *    field's current value matches this box's optionValue. If no value
 *    is set, fall back to `defaultChecked` (lets a template ship with a
 *    pre-selected option).
 *
 * 2. **Standalone** (no canonical link, no groupKey): tick when
 *    `defaultChecked` is true. The user can flip this per-job via the
 *    click-to-toggle PDF preview, which writes the override into the
 *    field-values map under the placement's synthetic key
 *    (`__cb__:<placementId>`).
 *
 * 3. **Linked boolean (legacy)** (`canonicalKey` set, no group): the
 *    bound field is a boolean and `checkboxValue` says which state this
 *    box represents — "checked" (default) or "unchecked". A Yes/No
 *    question uses one boolean field with two boxes. Falls through to
 *    `defaultChecked` when the field is unset.
 */
export function isCheckboxTicked(
  value: string | undefined,
  checkboxMode: string | undefined,
  opts: {
    placementId?: string;
    groupKey?: string;
    optionValue?: string;
    canonicalKey?: string;
    defaultChecked?: boolean;
    overrides?: Record<string, string>;
  } = {},
): boolean {
  const {
    placementId,
    groupKey,
    optionValue,
    canonicalKey,
    defaultChecked,
    overrides,
  } = opts;

  // Per-placement override from the click-to-toggle preview always wins.
  if (placementId && overrides) {
    const ov = overrides[`__cb__:${placementId}`];
    if (ov === "true" || ov === "1" || ov === "yes") return true;
    if (ov === "false" || ov === "0" || ov === "no") return false;
  }

  // Group member: match optionValue against the group's current value.
  if (groupKey) {
    const groupVal = (value ?? "").trim();
    if (groupVal) {
      return (
        groupVal.toLowerCase() === (optionValue ?? "").trim().toLowerCase()
      );
    }
    return !!defaultChecked;
  }

  // Linked boolean (legacy): keep checkboxMode + truthy/falsy semantics.
  if (canonicalKey) {
    const raw = (value ?? "").trim();
    if (raw === "") return !!defaultChecked;
    const lower = raw.toLowerCase();
    const fieldChecked = lower === "true" || lower === "yes" || lower === "1";
    return checkboxMode === "unchecked" ? !fieldChecked : fieldChecked;
  }

  // Standalone: pure default.
  return !!defaultChecked;
}

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/** The subset of pdf-lib's `PDFFont` the layout maths needs. */
export interface TextMetrics {
  heightAtSize(size: number, options: { descender: boolean }): number;
  widthOfTextAtSize(text: string, size: number): number;
}

/**
 * Compute the baseline y for vertically centering text inside a
 * rectangle.
 *
 * In pdf-lib, `drawText`'s `y` is the glyph baseline — not the bottom of
 * a box — so passing the rectangle's bottom (`fp.y`) makes glyphs sit at
 * the bottom edge of the box. The visual editor previews text centered
 * inside the rectangle, so the two would otherwise disagree (a value
 * placed in the centre of an editor box would render at the bottom of
 * the actual filled PDF).
 *
 * We approximate vertical centring using cap height (top of glyph to
 * baseline). For Latin text and digits this puts the visual centre of
 * the text at the visual centre of the rectangle.
 */
export function centerBaselineY(
  font: TextMetrics,
  size: number,
  fp: { y: number; height: number },
): number {
  const cap = font.heightAtSize(size, { descender: false });
  return fp.y + (fp.height - cap) / 2;
}

// ---------------------------------------------------------------------------
// Text layout inside a rectangle (MOR-2342)
// ---------------------------------------------------------------------------

/** Line spacing as a multiple of the font size. */
export const TEXT_LINE_HEIGHT_FACTOR = 1.2;
/** Preferred floor for `fitFontSizeToWidth` (a single line shrunk to width). */
export const MIN_FIT_FONT_SIZE = 6;
/**
 * Absolute floor for `layoutTextInBox`. Below this the text is no longer
 * legible in print, so the layout stops shrinking and reports `overflow`
 * instead. The fill then refuses to draw that placement at all (see
 * `describeUnfittableValue`); nothing is ever truncated or spilled.
 */
export const HARD_MIN_FIT_FONT_SIZE = 4;

/** Result of laying a value out inside a placement rectangle. */
export interface TextLayout {
  /** Lines to draw, top first. Never empty for a non-blank value. */
  lines: string[];
  /** Font size to draw at (≤ the requested size). */
  size: number;
  /** Baseline-to-baseline distance for multi-line output. */
  lineHeight: number;
  /**
   * True when even `HARD_MIN_FIT_FONT_SIZE` could not contain the value in
   * the rectangle. The full text is still returned so nothing is silently
   * lost, but callers must NOT draw it: the fill fails with an actionable
   * error and the editor preview flags the box for resizing.
   */
  overflow: boolean;
}

/**
 * Join a multi-line value into one line, comma-separated. A composed address
 * block ("street⏎suburb STATE postcode") is stored with newlines for letters;
 * a form's single-line box wants it inline.
 *
 * @example
 * collapseLinesInline("38 Lindsay St\nBurwood NSW 2134"); // "38 Lindsay St, Burwood NSW 2134"
 */
export function collapseLinesInline(text: string): string {
  return splitLines(text).join(", ");
}

/** True when the rectangle is only tall enough for one line at `size`. */
export function isSingleLineBox(fp: { height: number }, size: number): boolean {
  return fp.height <= 0 || fp.height < 2 * size * TEXT_LINE_HEIGHT_FACTOR;
}

/**
 * The largest size, no bigger than `size` and no smaller than
 * `MIN_FIT_FONT_SIZE`, at which `text` fits in `width`. Glyph widths scale
 * linearly with size, so one measurement suffices.
 */
export function fitFontSizeToWidth(
  text: string,
  width: number,
  size: number,
  font: Pick<TextMetrics, "widthOfTextAtSize">,
): number {
  if (width <= 0 || !text) return size;
  const measured = font.widthOfTextAtSize(text, size);
  if (measured <= width) return size;
  const fitted = Math.floor(((size * width) / measured) * 2) / 2;
  return Math.max(MIN_FIT_FONT_SIZE, Math.min(size, fitted));
}

/**
 * Lay a text value out inside its placement rectangle so that it is
 * contained in BOTH dimensions, without ever dropping text (MOR-2342).
 *
 * Source shape:
 * - A single-line box (too short for two lines at the requested size) is a
 *   one-line control: source line breaks collapse to ", " (an address block
 *   reads "street, suburb STATE postcode"). pdf-lib would otherwise start a
 *   new line per `\n` at its 24pt default spacing and drop the second line
 *   below the border, over the next label.
 * - A taller box is a genuine multi-line control: the source lines are kept.
 *
 * Containment policy, applied to either shape: try the requested size, then
 * half-point steps down to `HARD_MIN_FIT_FONT_SIZE`. At each size, lines are
 * word-wrapped to the box width (an unbroken token longer than the width is
 * broken between characters), and the first size at which every line fits
 * the width AND the stack fits the height wins. So a one-line box takes the
 * value on one shrunk line when it can, and only wraps onto extra lines when
 * a smaller single line would be needed than a two-line stack allows. If the
 * floor still cannot contain the value, the layout is returned at the floor
 * with `overflow: true`: the text is preserved for the error message, but the
 * fill refuses to draw it and records why, so no PDF ever ships with a value
 * spilling over its border or cut off.
 *
 * Pure: `font` supplies the metrics so this is unit-testable and the editor
 * preview can mirror it.
 */
export function layoutTextInBox(
  text: string,
  fp: { width: number; height: number },
  size: number,
  font: Pick<TextMetrics, "widthOfTextAtSize">,
): TextLayout {
  const sourceLines = isSingleLineBox(fp, size)
    ? [collapseLinesInline(text)]
    : splitLines(text);
  const layoutAt = (s: number) => ({
    lines: sourceLines.flatMap((line) =>
      wrapLineToWidth(line, fp.width, s, font),
    ),
    size: s,
    lineHeight: s * TEXT_LINE_HEIGHT_FACTOR,
  });
  const contained = candidateSizes(size)
    .map(layoutAt)
    .find((layout) => layoutFitsBox(layout, fp, font));
  if (contained) return { ...contained, overflow: false };
  return {
    ...layoutAt(Math.min(size, HARD_MIN_FIT_FONT_SIZE)),
    overflow: true,
  };
}

/** Requested size down to the hard floor in half-point steps. */
function candidateSizes(size: number): number[] {
  if (size <= HARD_MIN_FIT_FONT_SIZE) return [size];
  const steps = Math.floor((size - HARD_MIN_FIT_FONT_SIZE) * 2);
  return [
    ...Array.from({ length: steps + 1 }, (_, i) => size - i / 2),
    ...(size - steps / 2 > HARD_MIN_FIT_FONT_SIZE
      ? [HARD_MIN_FIT_FONT_SIZE]
      : []),
  ];
}

/**
 * Does a candidate layout sit inside the rectangle? A zero or negative
 * dimension means "unconstrained" on that axis. A single line only needs the
 * glyphs (≈ the font size) to fit the height; a stack needs its full
 * line-height rhythm to fit.
 */
export function layoutFitsBox(
  layout: Pick<TextLayout, "lines" | "size" | "lineHeight">,
  fp: { width: number; height: number },
  font: Pick<TextMetrics, "widthOfTextAtSize">,
): boolean {
  const widthOk =
    fp.width <= 0 ||
    layout.lines.every(
      (line) => font.widthOfTextAtSize(line, layout.size) <= fp.width,
    );
  const heightOk =
    fp.height <= 0 ||
    (layout.lines.length <= 1
      ? layout.size <= fp.height
      : layout.lines.length * layout.lineHeight <= fp.height);
  return widthOk && heightOk;
}

/**
 * The actionable error for a value that cannot be contained even at the
 * floor size (MOR-2342). The fill refuses to draw an overlapping or
 * truncated result; the practitioner enlarges or re-maps the box (or
 * shortens the value) and regenerates.
 */
export function describeUnfittableValue(
  fp: { canonicalKey: string; page: number; width: number; height: number },
  layout: Pick<TextLayout, "size" | "lines">,
  valueLength: number,
): string {
  return (
    `"${fp.canonicalKey}" (${valueLength} characters) does not fit its ` +
    `${Math.round(fp.width)}×${Math.round(fp.height)}pt box on page ${fp.page} ` +
    `even at ${layout.size}pt across ${layout.lines.length} line(s). ` +
    `Enlarge or re-map that field in the PDF field editor, or shorten the value, then regenerate.`
  );
}

/**
 * Baseline of the FIRST line when a multi-line layout starts at the top of
 * the box (subsequent lines step down by `lineHeight`). Pads the line so
 * its cap height sits centred within its own line box.
 */
export function topBaselineY(
  font: TextMetrics,
  layout: Pick<TextLayout, "size" | "lineHeight">,
  fp: { y: number; height: number },
): number {
  const cap = font.heightAtSize(layout.size, { descender: false });
  return fp.y + fp.height - cap - (layout.lineHeight - cap) / 2;
}

function splitLines(text: string): string[] {
  return text
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/**
 * Greedy word wrap of one line to `width`. A single token wider than the box
 * (a long reference number, an unbroken email) is broken between characters
 * so it too stays inside the width — never dropped.
 */
function wrapLineToWidth(
  line: string,
  width: number,
  size: number,
  font: Pick<TextMetrics, "widthOfTextAtSize">,
): string[] {
  if (width <= 0 || font.widthOfTextAtSize(line, size) <= width) return [line];
  const words = line
    .split(/\s+/)
    .filter(Boolean)
    .flatMap((word) => breakLongToken(word, width, size, font));
  return words.reduce<string[]>((acc, word) => {
    const last = acc[acc.length - 1];
    const joined = last === undefined ? word : `${last} ${word}`;
    if (last !== undefined && font.widthOfTextAtSize(joined, size) <= width) {
      return [...acc.slice(0, -1), joined];
    }
    return [...acc, word];
  }, []);
}

/** Split one over-wide token into the longest character runs that fit. */
function breakLongToken(
  token: string,
  width: number,
  size: number,
  font: Pick<TextMetrics, "widthOfTextAtSize">,
): string[] {
  if (font.widthOfTextAtSize(token, size) <= width) return [token];
  const chars = Array.from(token);
  return chars.reduce<string[]>((acc, ch) => {
    const last = acc[acc.length - 1];
    if (
      last !== undefined &&
      font.widthOfTextAtSize(last + ch, size) <= width
    ) {
      return [...acc.slice(0, -1), last + ch];
    }
    return [...acc, ch];
  }, []);
}

/** Left offset that centres a drawn item in its box, never going negative. */
export function centeredOffset(boxSize: number, drawSize: number): number {
  return Math.max(0, (boxSize - drawSize) / 2);
}

/** Centre x of the `index`-th character cell, for a glyph of `charWidth`. */
export function charCellX(
  boxX: number,
  cellWidth: number,
  index: number,
  charWidth: number,
): number {
  return boxX + (index + 0.5) * cellWidth - charWidth / 2;
}

/** The "X" mark's font size: 90% of the smaller box side, or the text size. */
export function checkboxMarkSize(
  fp: { width: number; height: number },
  fontSize: number,
): number {
  const w = fp.width > 0 ? fp.width : 12;
  const h = fp.height > 0 ? fp.height : 12;
  return Math.min(w, h) * 0.9 || fontSize;
}

/**
 * Scale an image to fit its box (never enlarging past 1:1) and apply the
 * signatory's own size multiplier.
 */
export function fitImageDraw(
  image: { width: number; height: number },
  box: { width: number; height: number },
  userScale: number,
): { width: number; height: number } {
  const scale =
    Math.min(box.width / image.width, box.height / image.height, 1) * userScale;
  return { width: image.width * scale, height: image.height * scale };
}

/** The overlay path's signature box: the rectangle's width, capped height. */
export function signatureOverlayBox(fp: { width: number }): {
  width: number;
  height: number;
} {
  return { width: fp.width || 160, height: 40 };
}

// ---------------------------------------------------------------------------
// Value bookkeeping shared by the fill paths
// ---------------------------------------------------------------------------

/**
 * Conditional fields (MOR-885): the keys whose values must not reach the
 * filled PDF, including the bridged PDF-field-name copies the wizard writes
 * for `pdfFieldMappings` (so the AcroForm/XFA/auto-fill paths can't pick a
 * suppressed value back up by field name).
 */
export function resolveSuppressedKeys(
  fieldConditions: FieldConditionMap | undefined,
  values: Record<string, string>,
  pdfFieldMappings: PdfFieldMapping[] | undefined,
): Set<string> {
  const suppressedKeys = resolveSuppressedFieldKeys(fieldConditions, values);
  for (const m of pdfFieldMappings ?? []) {
    if (m.canonicalKey && suppressedKeys.has(m.canonicalKey)) {
      suppressedKeys.add(m.pdfFieldName);
    }
  }
  return suppressedKeys;
}

/**
 * The values map the XFA / AcroForm paths fill from: everything except keys
 * a signature image already consumed and keys a condition suppressed.
 */
export function buildFillValues(
  values: Record<string, string>,
  handledSignatureKeys: ReadonlySet<string>,
  suppressedKeys: ReadonlySet<string>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(values).filter(
      ([key]) => !handledSignatureKeys.has(key) && !suppressedKeys.has(key),
    ),
  );
}

/** The values key a mapping reads: its canonical key, else the field name. */
export function mappingLookupKey(mapping: PdfFieldMapping): string {
  return mapping.canonicalKey || mapping.pdfFieldName;
}

/** True when a mapping describes a signature field either way round. */
export function isSignatureMapping(mapping: PdfFieldMapping): boolean {
  return (
    mapping.canonicalKey === "signature" || mapping.fieldType === "signature"
  );
}

/** A value bound to `signature` is drawn as one, whatever the mapping says. */
export function effectiveAcroFieldType(
  lookupKey: string,
  fieldType: string,
): string {
  return lookupKey === "signature" ? "signature" : fieldType;
}

/** The truthy strings the by-name auto-fill pass ticks a checkbox for. */
export function isAutoFillChecked(value: string): boolean {
  return (
    value === "true" || value === "yes" || value === "1" || value === "Yes"
  );
}

// ---------------------------------------------------------------------------
// Text overlay — what each placement should draw
// ---------------------------------------------------------------------------

/**
 * A date calc describes the FIELD's value, not one rectangle. A date split
 * across day/month/year boxes is several placements of the same canonical
 * key each drawing a slice of that value, so a calc configured on any one
 * of them drives the whole group — otherwise only the slice carrying the
 * calc would fill. The editor now writes the calc to every placement; this
 * map keeps templates configured before that fix working.
 */
export function buildDateCalcByKey(
  fieldPositions: PdfFieldPlacement[],
): Map<string, DateCalc> {
  const dateCalcByKey = new Map<string, DateCalc>();
  for (const fp of fieldPositions) {
    if (fp.dateCalc && fp.canonicalKey && !dateCalcByKey.has(fp.canonicalKey)) {
      dateCalcByKey.set(fp.canonicalKey, fp.dateCalc);
    }
  }
  return dateCalcByKey;
}

/** A signature placement resolves by canonical key, not by its field type. */
export function placementType(
  fp: PdfFieldPlacement,
  customFieldTypes?: Readonly<Record<string, string>>,
): string {
  return inferPlacementFieldType(fp, customFieldTypes);
}

/** Contact-field parts that hold a date. */
const DATE_CONTACT_PARTS: ReadonlySet<string> = new Set(["date_of_birth"]);

/**
 * The type a placement renders as (MOR-2342 BKY-08). The signature key is
 * always a signature and an explicit non-text type always wins. A placement
 * left as plain text (or untyped) but bound to a date field — a canonical
 * `date` field such as `appointment_date`, or a contact date of birth — is
 * a DATE: boxed day/month/year groups must slice the digits (28 · 09 · 2026),
 * not the raw "28/09/2026" (28 · /0 · 9/20). Ordinary text keeps its slashes.
 *
 * @example
 * inferPlacementFieldType({ canonicalKey: "appointment_date", fieldType: "text", ... }); // "date"
 * inferPlacementFieldType({ canonicalKey: "contact_job_date_of_birth", ... }); // "date"
 * inferPlacementFieldType({ canonicalKey: "company_name", fieldType: "text", ... }); // "text"
 */
export function inferPlacementFieldType(
  fp: Pick<PdfFieldPlacement, "canonicalKey" | "fieldType">,
  customFieldTypes?: Readonly<Record<string, string>>,
): string {
  if (fp.canonicalKey === "signature") return "signature";
  if (fp.fieldType && fp.fieldType !== "text") return fp.fieldType;
  if (CANONICAL_FIELDS_MAP[fp.canonicalKey]?.fieldType === "date") {
    return "date";
  }
  const contactPart = parseContactFieldKey(fp.canonicalKey)?.part;
  if (contactPart && DATE_CONTACT_PARTS.has(contactPart)) return "date";
  // A firm's own custom field typed `date` (its definition, not the
  // placement, carries the type).
  if (customFieldTypes?.[fp.canonicalKey] === "date") return "date";
  return fp.fieldType ?? "text";
}

/** The digits of DDMMYYYY. */
const DATE_DIGIT_COUNT = 8;

/**
 * Boxed day / month / year groups fed by an UNTYPED field (MOR-2342 BKY-08):
 * a legacy or custom "text" field on a third-party form whose value is a
 * complete date, split into cells that together address at most the eight
 * digits of DDMMYYYY (e.g. cells 2/2/4 at 0/2/4). Such cells can only ever
 * hold digits — a separator in a day-month-year box is never intended — so
 * the value is sliced as its digits rather than as "28", "/0", "9/20".
 *
 * Deliberately narrow: the value must be nothing but a REAL calendar date,
 * read day-first (ordinary slash-bearing text like "A/B 12", a partial
 * "1/2/34" and an impossible "31/02/2024" are untouched), and a layout whose
 * cells reach beyond eight characters (a ten-cell "DD/MM/YYYY" strip) keeps
 * the separators it was designed for.
 *
 * @example
 * boxedDateDigits("05/09/2021", { charStart: 4, charBoxes: 4 }); // "05092021"
 * boxedDateDigits("A/B 12", { charStart: 0, charBoxes: 5 }); // null
 * boxedDateDigits("28/09/2021", { charStart: 0, charBoxes: 10 }); // null
 */
export function boxedDateDigits(
  value: string,
  fp: { charStart?: number; charBoxes?: number },
): string | null {
  if ((fp.charStart ?? 0) + (fp.charBoxes ?? 0) > DATE_DIGIT_COUNT) return null;
  const calendar = parseCalendarDate(value);
  return calendar ? calendarDateDigits(calendar) : null;
}

/**
 * The actionable error for a template whose source PDF is encrypted
 * (MOR-2342 BKY-09 follow-up). pdf-lib can open such a file but cannot
 * decrypt its streams, so anything it "fills" is unreadable garbage. The fill
 * refuses instead of shipping a broken PDF.
 */
export function describeEncryptedSource(templateName: string): string {
  return (
    `The source PDF for "${templateName}" is encrypted (it carries an owner ` +
    `password), so Morgan cannot fill or flatten it and the result would not ` +
    `open. Upload a decrypted copy of the form to this template, then regenerate.`
  );
}

/**
 * The raw date a placement renders: an explicit value always wins; a date
 * calc fills the gap by deriving from another date field (e.g. 10 years
 * before the appointment date); auto-date keys fall back to today. "" when
 * the placement has no date to draw.
 * @internal — exported for unit tests
 */
export function resolvePlacementDate(
  fp: PdfFieldPlacement,
  value: string | undefined,
  opts: {
    values: Record<string, string>;
    dateCalcByKey: ReadonlyMap<string, DateCalc>;
    dateCalcContext: DateCalcContext;
  },
): string {
  return (
    value ||
    resolveDateCalc(
      fp.dateCalc ?? opts.dateCalcByKey.get(fp.canonicalKey),
      opts.values,
      opts.dateCalcContext,
    ) ||
    (AUTO_DATE_KEYS.has(fp.canonicalKey) ? todayIso() : "")
  );
}

/** What the caller should draw for one placement. */
export type PlacementDraw =
  /** Nothing — the placement is unaddressable, suppressed, or has no value. */
  | { kind: "skip" }
  /** Per-character cells: one glyph per cell, centred in `cellWidth`. */
  | { kind: "boxed"; slice: string; cellWidth: number }
  /** A line of text at the rectangle's left edge, vertically centred. */
  | { kind: "text"; text: string; maxWidth?: number }
  /** A checkbox, ticked or not — the caller logs either way. */
  | { kind: "checkbox"; ticked: boolean; value: string | undefined }
  /** A signature image the caller fetches and embeds. */
  | { kind: "signatureImage"; url: string; userScale: number };

/**
 * Decide what one visual placement renders, given the job's values.
 *
 * Mirrors the editor's own precedence: unaddressable and suppressed
 * placements draw nothing, boxed placements draw their slice of the value,
 * and everything else falls through to its field type.
 */
export function resolvePlacementDraw(
  fp: PdfFieldPlacement,
  opts: {
    values: Record<string, string>;
    suppressedKeys: ReadonlySet<string>;
    dateCalcByKey: ReadonlyMap<string, DateCalc>;
    dateCalcContext: DateCalcContext;
    pageCount: number;
    /** Field type per custom field key (a custom field typed `date`). */
    customFieldTypes?: Readonly<Record<string, string>>;
  },
): PlacementDraw {
  const { values, suppressedKeys, pageCount } = opts;
  const skip: PlacementDraw = { kind: "skip" };

  // Checkbox placements may have no canonical key — they can be
  // standalone (driven by `defaultChecked`) or part of a group keyed
  // by `groupKey`. Non-checkboxes still require a canonical key.
  const isCheckbox = fp.fieldType === "checkbox";
  if (!fp.canonicalKey && !isCheckbox) return skip;
  // Standalone checkboxes (no canonical, no group, no default) used to
  // be skipped — but the click-to-toggle preview writes per-placement
  // overrides under `__cb__:<id>` which `isCheckboxTicked` honours.
  // Keep the placement in the iteration when such an override exists
  // so the user's tick survives a re-fill.
  const hasOverride = isCheckbox && fp.id ? `__cb__:${fp.id}` in values : false;
  if (
    isCheckbox &&
    !fp.canonicalKey &&
    !fp.groupKey &&
    fp.defaultChecked === undefined &&
    !hasOverride
  ) {
    return skip;
  }
  const pageIndex = fp.page - 1;
  if (pageIndex < 0 || pageIndex >= pageCount) return skip;

  const type = placementType(fp, opts.customFieldTypes);
  // For groups, the value is looked up by groupKey, not canonicalKey.
  const lookupKey = isCheckbox && fp.groupKey ? fp.groupKey : fp.canonicalKey;
  // Conditional fields (MOR-885): skip the whole placement when its field
  // is suppressed, so default-checked boxes, date calcs, and auto-date
  // fallbacks can't render a value the condition says to withhold.
  if (lookupKey && suppressedKeys.has(lookupKey)) return skip;
  const value = lookupKey ? values[lookupKey] : undefined;
  const maxWidth = fp.width > 0 ? fp.width : undefined;

  // Boxed (per-character cell) rendering — used for ABN/ACN/TFN/postcode
  // rows and split DOB groups on third-party PDFs. Each rectangle covers
  // one contiguous group of cells; charStart/charBoxes select the slice
  // of the value that belongs in this group.
  if (
    fp.charBoxes &&
    fp.charBoxes > 0 &&
    fp.width > 0 &&
    type !== "checkbox" &&
    type !== "signature"
  ) {
    // Strip slashes so day/month/year split rectangles can address the
    // value via charStart 0/2/4 with charBoxes 2/2/4. For non-date types,
    // strip whitespace so a value like "12 345 678 901" still aligns one
    // digit per cell.
    const source: string | null =
      type === "date"
        ? (() => {
            const raw = resolvePlacementDate(fp, value, opts);
            return raw ? dateBoxDigits(raw) : null;
          })()
        : value && !isSignatureToken(value)
          ? (boxedDateDigits(value, fp) ?? value.replace(/\s+/g, ""))
          : null;
    if (!source) return skip;

    const start = fp.charStart ?? 0;
    const slice = source.slice(start, start + fp.charBoxes);
    if (!slice) return skip;

    return { kind: "boxed", slice, cellWidth: fp.width / fp.charBoxes };
  }

  switch (type) {
    case "date": {
      const raw = resolvePlacementDate(fp, value, opts);
      if (!raw) return skip;
      return { kind: "text", text: formatDateValue(raw) };
    }

    case "checkbox": {
      return {
        kind: "checkbox",
        ticked: isCheckboxTicked(value, fp.checkboxValue, {
          placementId: fp.id,
          groupKey: fp.groupKey,
          optionValue: fp.optionValue,
          canonicalKey: fp.canonicalKey || undefined,
          defaultChecked: fp.defaultChecked,
          overrides: values,
        }),
        value,
      };
    }

    case "signature": {
      if (isSignatureToken(value)) {
        return { kind: "signatureImage", ...parseSigToken(value as string) };
      }
      return value ? { kind: "text", text: value } : skip;
    }

    default: {
      return value && !isSignatureToken(value)
        ? { kind: "text", text: value, maxWidth }
        : skip;
    }
  }
}
